import { useState } from "react";
import styled from "styled-components";
import { Dropdown, Input, Modal, Tag, message } from "antd";
import type { MenuProps } from "antd";
import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";

import { MediaGroup } from "../../api/mediaGroupAPI";
import { GroupCounts, GroupFilter, UNGROUPED_FILTER } from "./mediaGroup";
import MediaGroupMembersModal, { GroupMemberItem } from "./modal/MediaGroupMembersModal";

interface Props {
  groups: MediaGroup[];
  counts: GroupCounts;
  active: GroupFilter;
  onChange: (next: GroupFilter) => void;
  hasEditAuth: boolean;
  /** 빈 그룹 생성 (미디어 배정은 다중 선택 → 그룹 지정에서) */
  onCreate?: (name: string) => Promise<void>;
  onRename?: (mediaGroupId: number, name: string) => Promise<void>;
  onDelete?: (mediaGroupId: number) => Promise<void>;
  /** 안내 문구에 쓰는 미디어 종류 이름 ("슬라이드" | "이미지" | "그로스 이미지") */
  itemLabel?: string;
  /** 그룹 편집 모달에 띄울 해당 종류 미디어 전체 (그룹 소속 여부 무관) */
  editableItems?: GroupCandidate[];
  /** 체크된 목록이 곧 그 그룹의 새 멤버 전체 */
  onEditMembers?: (mediaGroupId: number, mediaIds: number[]) => Promise<void>;
}

/** 그룹 편집 모달에 넘길 미디어 */
export interface GroupCandidate {
  mediaId: number;
  name: string;
  thumbnailUrl: string;
  mediaGroupId: number | null;
}

/**
 * 미디어 그룹 필터 + 관리 바.
 * 태그 클릭으로 전체 / 그룹없음 / 각 그룹을 오가고, 편집 권한이 있으면
 * 그룹 태그의 ⋯ 메뉴에서 이름 수정·삭제, 끝의 + 로 새 그룹을 만든다.
 */
const MediaGroupBar = ({
  groups,
  counts,
  active,
  onChange,
  hasEditAuth,
  onCreate,
  onRename,
  onDelete,
  itemLabel = "미디어",
  editableItems,
  onEditMembers,
}: Props) => {
  // editing: null = 닫힘, 0 = 신규 생성, 그 외 = 해당 그룹 이름 수정
  const [editingId, setEditingId] = useState<number | null>(null);
  const [nameValue, setNameValue] = useState("");
  const [saving, setSaving] = useState(false);
  // "그룹 편집" 모달의 대상 그룹
  const [membersTarget, setMembersTarget] = useState<MediaGroup | null>(null);

  const canManage = hasEditAuth && !!onCreate;

  // 그룹이 하나도 없고 만들 수도 없으면 바 자체를 숨긴다
  if (groups.length === 0 && !canManage) return null;

  const openCreate = () => {
    setEditingId(0);
    setNameValue("");
  };

  const openRename = (group: MediaGroup) => {
    setEditingId(group.mediaGroupId);
    setNameValue(group.name);
  };

  const submitName = async () => {
    const name = nameValue.trim();
    if (!name) {
      message.warning("그룹 이름을 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      if (editingId === 0) await onCreate?.(name);
      else if (editingId != null) await onRename?.(editingId, name);
      setEditingId(null);
    } catch (e: any) {
      message.error(e?.message || "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (group: MediaGroup) => {
    Modal.confirm({
      title: "그룹 삭제",
      content: `'${group.name}' 그룹을 삭제하시겠습니까? 속해 있던 미디어는 삭제되지 않고 '그룹없음' 상태가 됩니다.`,
      okText: "삭제",
      okButtonProps: { danger: true },
      cancelText: "취소",
      centered: true,
      onOk: async () => {
        try {
          await onDelete?.(group.mediaGroupId);
          // 삭제한 그룹을 보고 있었다면 전체로 되돌린다
          if (active === group.mediaGroupId) onChange(null);
        } catch (e: any) {
          message.error(e?.message || "삭제에 실패했습니다.");
        }
      },
    });
  };

  const canEditMembers = hasEditAuth && !!onEditMembers;

  const groupMenu = (group: MediaGroup): MenuProps => ({
    items: [
      ...(canEditMembers
        ? [
            {
              key: "members",
              label: "그룹 편집",
              icon: <SettingOutlined />,
              onClick: () => setMembersTarget(group),
            },
            { type: "divider" as const },
          ]
        : []),
      { key: "rename", label: "이름 수정", icon: <EditOutlined />, onClick: () => openRename(group) },
      { key: "delete", label: "그룹 삭제", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(group) },
    ],
  });

  // 그룹 편집 모달에는 해당 종류 전체를 넘긴다 (체크 = 멤버). 소속 그룹명은 이동 안내용.
  const memberItems: GroupMemberItem[] = (editableItems ?? []).map((it) => ({
    ...it,
    currentGroupName:
      it.mediaGroupId == null
        ? null
        : groups.find((g) => g.mediaGroupId === it.mediaGroupId)?.name ?? null,
  }));

  return (
    <>
      <Bar>
        {/* 그룹이 하나도 없으면 필터할 것이 없으므로 All/그룹없음 도 감추고, 생성 버튼만 남긴다 */}
        {groups.length > 0 && (
          <>
            <Tag
              color={active == null ? "blue" : "default"}
              onClick={() => onChange(null)}
              style={{ cursor: "pointer" }}
            >
              All <CountBadge $active={active == null}>{counts.total}</CountBadge>
            </Tag>

            {counts.ungrouped > 0 && (
              <Tag
                color={active === UNGROUPED_FILTER ? "blue" : "default"}
                onClick={() => onChange(active === UNGROUPED_FILTER ? null : UNGROUPED_FILTER)}
                style={{ cursor: "pointer" }}
              >
                그룹없음{" "}
                <CountBadge $active={active === UNGROUPED_FILTER}>{counts.ungrouped}</CountBadge>
              </Tag>
            )}
          </>
        )}

        {groups.map((group) => {
          const isActive = active === group.mediaGroupId;
          const count = counts.byGroup[group.mediaGroupId] ?? 0;
          return (
            <Tag
              key={group.mediaGroupId}
              color={isActive ? "blue" : "default"}
              onClick={() => onChange(isActive ? null : group.mediaGroupId)}
              style={{ cursor: "pointer" }}
            >
              {group.name} <CountBadge $active={isActive}>{count}</CountBadge>
              {hasEditAuth && (onRename || onDelete) && (
                <Dropdown menu={groupMenu(group)} trigger={["click"]}>
                  <ManageHandle onClick={(e) => e.stopPropagation()} aria-label="그룹 관리">
                    <MoreOutlined />
                  </ManageHandle>
                </Dropdown>
              )}
            </Tag>
          );
        })}

        {canManage && (
          <Tag onClick={openCreate} style={{ cursor: "pointer", borderStyle: "dashed" }}>
            <PlusOutlined /> 그룹
          </Tag>
        )}
      </Bar>

      <Modal
        open={editingId != null}
        title={editingId === 0 ? "새 그룹 만들기" : "그룹 이름 수정"}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onOk={submitName}
        onCancel={() => setEditingId(null)}
        centered
        destroyOnClose
        width={380}
      >
        <Input
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onPressEnter={submitName}
          placeholder="그룹 이름"
          maxLength={255}
          autoFocus
        />
      </Modal>

      {membersTarget && (
        <MediaGroupMembersModal
          open
          mediaGroupId={membersTarget.mediaGroupId}
          groupName={membersTarget.name}
          itemLabel={itemLabel}
          items={memberItems}
          onCancel={() => setMembersTarget(null)}
          onSubmit={async (mediaIds) => {
            await onEditMembers?.(membersTarget.mediaGroupId, mediaIds);
            setMembersTarget(null);
          }}
        />
      )}
    </>
  );
};

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;

  /* Tag 안에 관리 핸들을 넣기 위해 내용물을 가로 정렬 */
  .ant-tag {
    display: inline-flex;
    align-items: center;
    margin-inline-end: 0;
  }
`;

const ManageHandle = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  padding: 0 1px;
  border-radius: 3px;
  opacity: 0.55;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06);
  }
`;

const CountBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  background: ${({ $active }) => ($active ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.08)")};
  border-radius: 8px;
  padding: 0 5px;
  font-size: 11px;
  margin-left: 3px;
  line-height: 1.4;
`;

export default MediaGroupBar;
